import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  // Send the HttpOnly `vqms_access_token` session cookie on every request.
  // The token is set by the backend on login/register/accept-invite/google-session
  // and never lives in JS-accessible storage — this eliminates XSS token theft.
  withCredentials: true,
});

// Read a cookie value by name — used to fetch the double-submit CSRF token
// that the backend sets on login (`vqms_csrf_token`). This cookie is NOT
// HttpOnly by design; the security guarantee comes from the fact that
// cross-origin JavaScript cannot read it (SameSite=None + Secure).
export const readCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : null;
};

// Double-submit CSRF: for state-changing methods, echo the CSRF cookie back
// as an X-CSRF-Token header so the backend middleware can verify it matches.
// Auth itself rides on the HttpOnly `vqms_access_token` cookie — no tokens
// in JS-accessible storage.
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (["POST", "PATCH", "PUT", "DELETE"].includes(method)) {
    const csrf = readCookie("vqms_csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    // Never swallow 401 — route guards handle. But surface 403/500/502 with a
    // toast so callers that forget try/catch don't silently fail.
    if (status === 403) {
      toast.error(
        typeof detail === "string" && detail
          ? detail
          : "You don't have permission for this action. Ask a workspace Admin to assign a role.",
        { id: "auth-403" }
      );
    } else if (status >= 500) {
      toast.error(
        typeof detail === "string" && detail ? detail : "Server error — please retry.",
        { id: "server-error" }
      );
    }
    return Promise.reject(err);
  }
);
